import React, {createContext, useContext, useEffect, useMemo, useState} from "react";
import {DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors} from "@dnd-kit/core";
import {restrictToParentElement, restrictToVerticalAxis} from "@dnd-kit/modifiers";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {SortablePropsInterface} from "./Sortable.props.interface";

const SortableItemContext = createContext<any>({});

export const Sortable = ({data, onChange, children}: SortablePropsInterface) => {
    const [items, setItems] = useState(() => data.map((item: any, itemIdx: number) => ({hash: itemIdx + 1, data: item})));
    const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates}));

    useEffect(() => {
        handleUpdateItems()
    }, [data]);

    const handleDrag = ({active, over}: any) => {
        const activeIndex = items.findIndex((item: any) => item.hash === active.id);
        const overIndex = items.findIndex((item: any) => item.hash === over?.id);
        const values = arrayMove(items, activeIndex, overIndex);

        setItems(values);
        onChange(values.map(({data}: any) => data));
    };

    const handleUpdateItems = () => {
        setItems(data.map((item: any, itemIdx: number) => ({hash: itemIdx + 1, data: item})));
    }

    return (
        <DndContext
            sensors={sensors}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={handleDrag}>
            <SortableContext items={items.map((item: any) => item.hash)} strategy={verticalListSortingStrategy}>
                {React.Children.map(children, (child, index) => (
                    items[index] && <Sortable.Item key={items[index].hash} hash={items[index].hash}>
                        {child}
                    </Sortable.Item>
                ))}
            </SortableContext>
        </DndContext>
    );
};

const SortableItem = ({children, hash}: any) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({id: hash} as any);

    const context = useMemo(() => ({
        attributes,
        listeners,
        isDragging,
        ref: setActivatorNodeRef
    }), [attributes, listeners, setActivatorNodeRef]);

    return (
        <SortableItemContext.Provider value={context}>
            <div ref={setNodeRef} style={{
                transform: CSS.Translate.toString(transform),
                transition,
                position: 'relative',
                zIndex: isDragging ? 1 : 'auto',
            }}>
                {children}
            </div>
        </SortableItemContext.Provider>
    );
};

Sortable.Item = SortableItem;
Sortable.DragHandle = ({children}: any) => {
    const {attributes, listeners, ref, isDragging} = useContext(SortableItemContext);
    return <div
        style={{cursor: isDragging ? 'grabbing' : 'grab', width: "fit-content", touchAction: 'none'}}
        {...attributes}
        {...listeners}
        ref={ref}>
        {children}
    </div>
};

export default Sortable
